export const zeroDecimalCurrencies = [
  'bif',
  'byr',
  'clp',
  'djf',
  'gnf',
  'isk',
  'jpy',
  'kmf',
  'krw',
  'pyg',
  'rwf',
  'ugx',
  'vnd',
  'vuv',
  'xaf',
  'xag',
  'xau',
  'xba',
  'xbb',
  'xbc',
  'xbd',
  'xdr',
  'xof',
  'xpd',
  'xpf',
  'xpt',
  'xts',
];

export const maybeConvertAmount = (amount: number, currency: string) => {
  return isZeroDecimal(currency) ? amount : amount / 100;
};

export const isZeroDecimal = (currency: string) => {
  return zeroDecimalCurrencies.includes(currency?.toLowerCase?.());
};

// Cached per locale — building an Intl.NumberFormat is costly and this is read
// on every keystroke via render(). The locale doesn't change within a session.
const separatorCache: Record<string, string> = {};

/**
 * Get the decimal separator for a locale (e.g. "." for en-US, "," for de-DE).
 *
 * Note some locales use non-Latin marks (e.g. "٫" for fa/ar-EG); callers that
 * feed the price input should normalize to "." or "," (see {@link formatLocaleNumber}).
 *
 * @param {string} [locale] BCP 47 locale tag. Omit to use the runtime default.
 * @return {string} The locale's decimal separator, falling back to ".".
 */
export const getDecimalSeparator = (locale?: string): string => {
  const key = locale ?? '';
  if (!(key in separatorCache)) {
    try {
      separatorCache[key] = new Intl.NumberFormat(locale).formatToParts(1.1).find(part => part.type === 'decimal')?.value || '.';
    } catch (e) {
      // Intl throws a RangeError on malformed tags — the locale can come from
      // server data (determine_locale() is filterable), so never let it break render.
      separatorCache[key] = '.';
    }
  }

  return separatorCache[key];
};

/**
 * The locale to read and write price separators with.
 *
 * Site locale before browser locale: which mark groups thousands is a property
 * of the shop, not of who is visiting it, so a German shop must read "1.500" as
 * 1500 for a shopper on a US-English browser too.
 *
 * `scData` carries the site locale but is enqueued only `whenRendered` for a
 * handful of legacy Stencil blocks (see AssetsService::enqueueComponents), so a
 * next-gen product page can have none — hence the `lang` fallback, which
 * WordPress renders from the site locale on every page.
 *
 * @return {string|undefined} BCP 47 locale tag, or undefined to use the runtime default.
 */
export const getInputLocale = (): string | undefined => {
  return window?.scData?.locale || document?.documentElement?.lang || navigator?.language;
};

/**
 * The mark a locale uses as its decimal separator, reduced to "." or ",".
 *
 * "٫" (fa, ar-EG) collapses to "," because parsing normalizes it to one before
 * any of this is consulted.
 *
 * @param {string} [locale] BCP 47 locale tag. Omit to use the runtime default.
 * @return {string} Either "." or ",".
 */
const decimalRole = (locale?: string): string => (getDecimalSeparator(locale) === '.' ? '.' : ',');

// One separator with exactly three digits after it: thousands grouping in a
// comma locale, three decimal places in a dot locale. Nothing in the string
// itself distinguishes them.
const AMBIGUOUS_GROUPING = /^\d{1,3}([.,])\d{3}$/;

/**
 * Parse a user-typed amount, accepting ".", "," or "٫" as the decimal mark
 * and Latin, Arabic-Indic, or Persian digits.
 *
 * Structurally invalid input returns NaN rather than being coerced — stripping
 * an unrecognized character can silently merge digits into a wrong amount
 * (e.g. "9٫90" read as 990, a 100x overcharge). A bare trailing separator
 * ("9,") reads as the integer, since this runs on every keystroke.
 *
 * "9,90" -> 9.9 · "٩٫٩٠" -> 9.9 · "1,000" -> 1000 · "1.234,56" -> 1234.56 · "1.2.3" -> NaN
 *
 * @param {string} value The raw input value.
 * @param {string} [locale] BCP 47 locale tag, used only to settle "1.500". Omit for the runtime default.
 * @return {number} The parsed amount, or NaN when invalid.
 */
export const parseLocaleFloat = (value: string, locale?: string): number => {
  if (typeof value !== 'string') return NaN;

  const clean = value
    .replace(/[٠-٩]/g, d => String(d.charCodeAt(0) - 0x0660)) // Arabic-Indic digits -> ASCII.
    .replace(/[۰-۹]/g, d => String(d.charCodeAt(0) - 0x06f0)) // Persian digits -> ASCII.
    .replace(/٫/g, ',') // U+066B, Arabic decimal separator.
    .replace(/^[\s\p{Sc}]+|[\s\p{Sc}]+$/gu, ''); // Currency symbols and padding — edges only.

  // Settle "1.500" by locale, not by shape. Where the separator is the locale's
  // decimal mark the value has three decimal places, which no supported currency
  // has — so reject it. Assuming "thousands" instead would read a mistyped 9.999
  // as 9999, the same silent 1000x this parser exists to prevent.
  const ambiguous = clean.match(AMBIGUOUS_GROUPING);
  if (ambiguous && ambiguous[1] === decimalRole(locale)) return NaN;

  // A plain number, a bare leading decimal (".5"), or 3-digit-grouped thousands
  // — each with 0-2 decimals. Grouping marks (٬, apostrophe, spaces) count only
  // inside that structure: an inner stray like "9 90" or "9€90" is NaN, never a
  // glued 990.
  if (!/^(\d+([.,]\d{0,2})?|[.,]\d{1,2}|\d{1,3}([.,٬'\s]\d{3})+([.,]\d{0,2})?)$/u.test(clean)) return NaN;

  // The last separator is the decimal mark only when 1-2 digits follow it,
  // so "1,000" stays a thousand instead of collapsing to 1.
  const grouped = clean.replace(/[٬'\s]/gu, '');
  const digits = grouped.replace(/[.,]/g, '');
  const decimal = grouped.match(/[.,](\d{1,2})$/);

  if (!decimal) return parseFloat(digits);
  return parseFloat(`${digits.slice(0, -decimal[1].length)}.${decimal[1]}`);
};

// Built from fragments so the digit class stays defined once.
const PATTERN_DIGIT = '[\\d٠-٩۰-۹]';
const PATTERN_EDGE = '[\\s\\p{Sc}]*';

/**
 * HTML `pattern` for price inputs — a coarse first filter, not the authority.
 *
 * It must accept everything {@link parseLocaleFloat} accepts (contract-tested in
 * currency.spec.ts); a narrower pattern would block values the parser reads
 * correctly. It is deliberately wider: the parser rejects "1.500" in a dot
 * locale, which one locale-blind attribute cannot express, so callers surface
 * the parser's NaN themselves rather than relying on this.
 * Needs the browser's u/v pattern flag for \p{Sc}; sc-input drops it when it
 * cannot compile, leaving the parser as the sole guard.
 */
export const PRICE_INPUT_PATTERN = `${PATTERN_EDGE}(${PATTERN_DIGIT}+([.,٫]${PATTERN_DIGIT}{0,2})?|[.,٫]${PATTERN_DIGIT}{1,2}|${PATTERN_DIGIT}{1,3}([.,٬'\\s]${PATTERN_DIGIT}{3})+([.,٫]${PATTERN_DIGIT}{0,2})?)${PATTERN_EDGE}`;

/**
 * {@link PRICE_INPUT_PATTERN}, or undefined where the browser cannot compile it.
 *
 * For raw `<input>` consumers that have no sc-input wrapper to guard them. An
 * uncompilable `pattern` makes the browser skip validation for the whole field,
 * so a required/min check would silently stop applying too.
 *
 * @return {string|undefined} The pattern, or undefined when it cannot compile.
 */
export const getValidPricePattern = (): string | undefined => {
  // The same regex flag the browser uses for the `pattern` attribute.
  const flag = 'unicodeSets' in RegExp.prototype ? 'v' : 'u';

  try {
    // Constructing it is the check — it throws where the flag or \p{Sc} is unsupported.
    new RegExp(`^(?:${PRICE_INPUT_PATTERN})$`, flag);
    return PRICE_INPUT_PATTERN;
  } catch {
    return undefined;
  }
};

/**
 * Format a numeric amount for a price input (no grouping).
 *
 * Only ever emits "." or "," — the separators {@link parseLocaleFloat} and the
 * input pattern understand. A comma locale gets ",", everything else gets ".",
 * so non-Latin marks like "٫" (fa, ar-EG) can't be shown and then fail to parse.
 *
 * @param {number} value  The amount in display units.
 * @param {string} [locale] BCP 47 locale tag. Omit to use the runtime default.
 * @return {string} The formatted number, or "" when the value is NaN.
 */
export const formatLocaleNumber = (value: number, locale?: string): string => {
  if (isNaN(value)) return '';
  const separator = getDecimalSeparator(locale) === ',' ? ',' : '.';
  return value.toString().replace('.', separator);
};
